import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    NgModule,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    QueryList,
    SimpleChanges,
    TemplateRef,
    ViewContainerRef,
} from '@angular/core';
import { tap } from 'rxjs/operators';

import { takeUntilDestroy } from '../../../../common/take-until-destroy.decorator';
import { ColumnId } from '../main/model/column-id.model';

import {
    autoFit,
    bindWidthResize,
    Context,
    DivRenderer,
    DivRendererOptions,
    Grid,
    GridNode,
    RenderContext,
    Row,
    Sort,
    syncCellsToColumns,
} from './experimental.grid';

@Directive({ selector: 'ng-template[gridContent]' })
export class GridContent {
    @Input('data-type') type: 'grid' | 'row' | 'cell';
    @Input('data-id') id: string;

    constructor(public templateRef: TemplateRef<any>) {}
}

export type SelectTemplateFn = (
    context: Context,
    contents: QueryList<GridContent>,
) => GridContent;

@Component({
    selector: '[ngGrid]',
    template: '',
    exportAs: 'grid',
})
export class ExperimentalNgGridComponent
    implements OnChanges, AfterContentInit, OnDestroy
{
    @ContentChildren(GridContent)
    private readonly _gridContents: QueryList<GridContent>;

    @Input() rendererOptions: DivRendererOptions;
    @Input() selectTemplate: SelectTemplateFn;
    @Input() grid: Grid;
    @Input() rowsTop: Row[];
    @Input() rows: Row[];
    @Input() sortDefs: Sort[];
    renderer: DivRenderer;
    gridNode: GridNode;
    @Output() willInflate: EventEmitter<void>;
    @Output() didInflate: EventEmitter<void>;
    private _didContentInit: boolean;
    private _isInflating: boolean;

    constructor(
        private readonly _viewContainerRef: ViewContainerRef,
        private readonly _ngZone: NgZone,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _el: ElementRef<HTMLElement>,
    ) {
        this.willInflate = new EventEmitter();
        this.didInflate = new EventEmitter();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this._didContentInit) {
            return;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('rendererOptions') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('selectTemplate') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('grid')
        ) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._deflate()
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                .then(() => {
                    return this._inflate();
                });
            return;
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        Promise.resolve()
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (changes.hasOwnProperty('rowsTop')) {
                    return this._setRowsTop();
                }
            })
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (changes.hasOwnProperty('rows')) {
                    return this._setRows();
                }
            })
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (
                    // eslint-disable-next-line no-prototype-builtins
                    changes.hasOwnProperty('rowsTop') ||
                    // eslint-disable-next-line no-prototype-builtins
                    changes.hasOwnProperty('rows')
                ) {
                    return (
                        this.renderer
                            .layout()
                            .catch()
                            // eslint-disable-next-line @typescript-eslint/promise-function-async
                            .then(() => syncCellsToColumns(this.renderer))
                    );
                }
            })
            .then(() => {
                // eslint-disable-next-line no-prototype-builtins
                if (changes.hasOwnProperty('sortDefs')) {
                    return (
                        this._setSortDefs()
                            // eslint-disable-next-line @typescript-eslint/promise-function-async
                            .then(() => this.renderer.layout().catch())
                    );
                }
            });
    }

    ngAfterContentInit(): void {
        this._didContentInit = true;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._inflate();
    }

    ngOnInit(): void {
        bindWidthResize(this._el.nativeElement.parentElement)
            .pipe(
                tap(() => {
                    if (
                        this._el.nativeElement.getBoundingClientRect()
                            .height !== 0
                    ) {
                        this._onResize();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.willInflate.complete();
        this.didInflate.complete();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._deflate();
    }

    public onResize(): void {
        this._onResize();
    }

    private _render(context: Context): RenderContext {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const content = this.selectTemplate
            ? this.selectTemplate(context, this._gridContents)
            : this._gridContents.find((e) => e.type === context.t);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!content || !content.templateRef) {
            return;
        }
        const ref = content.templateRef.createEmbeddedView(context);
        return {
            element: ref.rootNodes[0],
            detectChanges: () => ref.detectChanges(),
            destroy: () => ref.destroy(),
        };
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _setRowsTop(): Promise<void> {
        try {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!this._isInflating && this.gridNode && this.rowsTop) {
                return this.gridNode.rowsTop
                    .setChildren(this.rowsTop)
                    .then(() => {});
            }
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return Promise.resolve();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _setRows(): Promise<void> {
        try {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!this._isInflating && this.gridNode && this.rows) {
                return this.gridNode.setChildren(this.rows).then(() => {});
            }
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return Promise.resolve();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _setSortDefs(): Promise<void> {
        try {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!this._isInflating && this.gridNode && this.sortDefs) {
                return this.gridNode.sort(this.sortDefs);
            }
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return Promise.resolve();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _inflate(): Promise<void> {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!this.rendererOptions || !this._gridContents || this._isInflating) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/promise-function-async
        const complete = (): Promise<void> => {
            try {
                const el: HTMLElement =
                    this._viewContainerRef.element.nativeElement;
                el.appendChild(this.gridNode.renderContext.element);
            } catch (e) {
                // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
            }
            this._isInflating = false;
            return (
                this._setRowsTop()
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this._setRows();
                    })
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this.renderer.layout().catch();
                    })
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this._setSortDefs();
                    })
                    // eslint-disable-next-line @typescript-eslint/promise-function-async
                    .then(() => {
                        return this.renderer.layout().catch();
                    })
                    .then(() => {
                        this._onDidInflate();
                        this.didInflate.emit();
                    })
            );
        };
        this._onWillInflate();
        this.willInflate.emit();
        this._isInflating = true;
        try {
            const rendererOpts: DivRendererOptions = {
                ...this.rendererOptions,
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                columns: this.rendererOptions.columns || [], // TODO remove. have underlying grid handle null/unset/missing columns
                defaultRenderFn: (context: Context) => {
                    return this._ngZone.runTask(() => {
                        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                        if (this.rendererOptions.defaultRenderFn) {
                            return this.rendererOptions.defaultRenderFn(
                                context,
                            );
                        }
                        return this._render(context);
                    });
                },
            };
            this.renderer = new DivRenderer(rendererOpts);
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            this.gridNode = this.renderer.grid(this.grid || {});
            this.renderer.gridNode = this.gridNode;
            this.renderer.render(this.gridNode);
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        return complete();
    }

    // eslint-disable-next-line @typescript-eslint/promise-function-async
    private _deflate(): Promise<void> {
        try {
            const el: HTMLElement =
                this._viewContainerRef.element.nativeElement;
            el.removeChild(this.gridNode.renderContext.element);
        } catch (e) {
            // CMPS_APP_DEF.ENV !== 'prod' &&  console.warn(e);
        }
        let prom = Promise.resolve();
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (this.renderer) {
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            prom = prom.then(() => this.renderer.destroy(this.gridNode));
        }
        return prom.then(() => {
            this.gridNode = null;
            this.renderer = null;
        });
    }

    private _onWillInflate(): void {
        this._el.nativeElement.style.visibility = 'hidden';
    }

    private _onDidInflate(): void {
        // this._changeDetectorRef.detectChanges();
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!this.renderer) {
            return;
        }

        // const flatColumns = this.rendererOptions.columns.reduce<Column[]>(
        //     (p, c) => {
        //         return p.concat(c.children);
        //     },
        //     [],
        // );

        // this is dirty. will try to improve the grid api
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.renderer
            .layout()
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            // .then(() => autoSizeColumns(flatColumns, this.renderer))
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this.renderer.layout())
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            // .then(() =>
            //     autoSizeRows(
            //         [
            //             ...[
            //                 ...this.renderer.contentColumns.columns.children,
            //             ].reverse(),
            //             // ...this.renderer.rows
            //         ],
            //         this.renderer,
            //         null,
            //         {},
            //     ),
            // )
            .then(() => {
                const left = this.renderer.rowcache.left.filter((e) =>
                    this.renderer.rows.some((ee) => ee.def === e.def),
                );
                left.forEach((e) => {
                    if (e.renderContext?.element != null) {
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        e.renderContext.element.style.height = `${e.def.height}px`;
                    }
                });
            })
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this.renderer.layout())
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            // .then(() => autoFit(this.renderer))
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() =>
                autoFit(
                    this.renderer,
                    null,
                    450 +
                        10 /* see margin and border for .cmps-coaching-wp-table--row* */,
                    null,
                    this.rendererOptions.columns.filter(
                        (e) => e.id === ColumnId.DISTRIBUTION,
                    ),
                ),
            )
            // eslint-disable-next-line @typescript-eslint/promise-function-async
            .then(() => this.renderer.layout())
            .catch((err) => console.warn(err))
            .then(() => {
                this._el.nativeElement.style.visibility = '';
            });
    }

    private _onResize(): void {
        this._onWillInflate();
        this._onDidInflate();
    }
}

@NgModule({
    declarations: [GridContent, ExperimentalNgGridComponent],
    exports: [GridContent, ExperimentalNgGridComponent],
})
export class ExperimentalNgGridModule {}
